import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
	BUTTON_TYPES,
	ChevronDown,
	DropdownPosition,
	EvDropdownMenu,
	EvEllipsedTextWithTooltip,
	EvFlexRow,
	EvLabelDefault,
	EvSpinner,
	EvSvgIcon,
	IDropdownMenuOption
} from '@evinced-private/ui-common';

import { getViewsOptionsList as createViewsOptionsList } from '../../../helpers/PropertyViewsHelper';
import { RoutesHelper } from '../../../helpers/RoutesHelper';
import { IPropertyView } from '../../../interfaces/PropertyViews';
import {
	IPropertyViewContext,
	usePropertyViewContext
} from '../../../providers/PropertyViewProvider';
import { useUserTenant } from '../../../providers/userTenantProvider/UserTenantProvider';

import {
	ALL_CONTENT_VIEW,
	manageViewsButtonTitle,
	VIEWS_LABEL,
	VIEWS_TRIGGER_BUTTON_ID
} from './consts/PropertyViews';

import './EvPropertyViews.scss';

interface IEvPropertyViewsProps {
	propertyId: string;
}

export const EvPropertyViews: React.FC<IEvPropertyViewsProps> = ({ propertyId }) => {
	const propertyViewContext: IPropertyViewContext = usePropertyViewContext();
	const currentViewName: string = propertyViewContext.currentView?.name || 'Select';
	const history = useHistory();
	const { isReadOnlyUser } = useUserTenant();

	const [propertyViewsOptions, setPropertyViewsOptions] = useState<IDropdownMenuOption[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const onOptionChange = useCallback(
		async (view: IPropertyView): Promise<void> => {
			await propertyViewContext.updateSelectedView(view);
		},
		[propertyViewContext]
	);

	const getViewsOptionsList = useCallback((): void => {
		const viewsOptions: IDropdownMenuOption[] = createViewsOptionsList(
			[ALL_CONTENT_VIEW, ...propertyViewContext.propertyViews],
			onOptionChange
		);

		setPropertyViewsOptions(viewsOptions);
		setIsLoading(false);
	}, [propertyViewContext.propertyViews, onOptionChange]);

	useEffect(() => {
		getViewsOptionsList();
	}, [getViewsOptionsList]);

	return (
		<EvFlexRow align="BASELINE" gap="LARGE" width="auto">
			<EvLabelDefault as="label" htmlFor={VIEWS_TRIGGER_BUTTON_ID}>
				{VIEWS_LABEL}
			</EvLabelDefault>
			{isLoading ? (
				<EvSpinner />
			) : (
				<EvDropdownMenu
					id="manage-views"
					position={DropdownPosition.BOTTOM_LEFT}
					options={[...propertyViewsOptions]}
					className="manage-views"
					stickyButtons={
						!isReadOnlyUser() && [
							{
								title: manageViewsButtonTitle,
								type: BUTTON_TYPES.PRIMARY,
								onClick: () => {
									history.push(RoutesHelper.getManageViewsPath(propertyId));
								},
								isFullWidth: true,
								children: manageViewsButtonTitle
							}
						]
					}
					triggerButtonProps={{
						id: VIEWS_TRIGGER_BUTTON_ID,
						dataAutomationId: VIEWS_TRIGGER_BUTTON_ID,
						type: BUTTON_TYPES.GHOST,
						title: `Views selection. Currently on view ${currentViewName}`,
						children: (
							<EvFlexRow aria-hidden="true" justify="SPACE_BETWEEN" padding="SMALL" width="250px">
								<EvEllipsedTextWithTooltip text={currentViewName} showTooltipOnlyOnEllipsed />
								<EvSvgIcon icon={ChevronDown} isSmall />
							</EvFlexRow>
						)
					}}
				/>
			)}
		</EvFlexRow>
	);
};
